import { type BottleUnit, fromOz } from "@babia/checks";
import { lazy, Suspense } from "react";
import { type BabyHours, niceMax } from "../../lib/overview.ts";
import type { Baby } from "../../lib/queries.ts";
import { hourSpan } from "../../lib/time.ts";
import { PerBaby, Section } from "./section.tsx";

// ECharts loads with the first chart, not with the log (see trends.tsx).
const HourBars = lazy(() => import("./hour-bars.tsx").then((m) => ({ default: m.HourBars })));

const quiet = "mt-3 py-3 text-[14px] italic opacity-60";

/** Every hour in the chart as text, noon first: the chart's plain-text twin. */
function HourTable({ hours, unit }: { hours: BabyHours; unit: BottleUnit }) {
  const cell = "py-1.5 pr-2 text-right";
  const amount = (oz: number | null) => (oz === null ? "—" : fromOz(oz, unit));
  return (
    <details className="group mt-2">
      <summary className="ghost-btn -ml-2 inline-flex min-h-[44px] cursor-pointer items-center px-2 text-[14px]">
        <span className="group-open:hidden">Show the table</span>
        <span className="hidden group-open:inline">Hide the table</span>
      </summary>
      <table className="w-full border-collapse text-left text-[13px] tabular-nums">
        <thead>
          <tr className="border-b border-ink text-[11px] uppercase tracking-[0.06em]">
            <th className="py-1.5 pr-2 font-normal opacity-60">Hour</th>
            <th className={`${cell} font-normal opacity-60`}>Bottles</th>
            <th className={`${cell} font-normal opacity-60`}>{unit}</th>
            <th className="py-1.5 text-right font-normal opacity-60">{unit} a bottle</th>
          </tr>
        </thead>
        <tbody>
          {hours.rows.map((r) => (
            <tr key={r.hour} className="border-t border-rule">
              <th scope="row" className="py-1.5 pr-2 font-normal">
                {hourSpan(r.hour)}
              </th>
              <td className={cell}>{r.bottles}</td>
              <td className={cell}>{r.bottles ? amount(r.oz) : "—"}</td>
              <td className="py-1.5 text-right">{amount(r.avgOz)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-ink">
            <th scope="row" className="py-1.5 pr-2 font-normal">
              All hours
            </th>
            <td className={cell}>{hours.bottles}</td>
            <td className={cell}>{amount(hours.oz)}</td>
            <td className="py-1.5 text-right">{amount(hours.avgOz)}</td>
          </tr>
        </tfoot>
      </table>
    </details>
  );
}

/**
 * Each baby's bottles in each hour of the day over the last two weeks: which
 * hours the baby feeds most, for spotting a rhythm or planning sleep
 * training. Noon to noon, like the sleep chart, so the night is one block. Only
 * full days count, as in the report (`bottleHours`). Twins share one scale.
 */
export function BottlesByHour({
  babies,
  hours,
  unit,
  loading,
  limited,
}: {
  babies: Baby[];
  /** Each baby's hours. */
  hours: ReadonlyMap<string, BabyHours>;
  /** The unit bottles show in. */
  unit: BottleUnit;
  /** The two weeks are still loading: the live list alone would undercount. */
  loading: boolean;
  /** The two weeks never loaded: only the live two days are here. */
  limited: boolean;
}) {
  const max = niceMax(
    Math.max(0, ...[...hours.values()].flatMap((h) => h.rows.map((r) => r.bottles))),
  );

  return (
    <Section title="Bottles by hour" note={limited ? "Yesterday only" : "The last two weeks"}>
      {!babies.length ? null : loading ? (
        <p className={quiet}>Loading the last two weeks…</p>
      ) : (
        <>
          <p className="mt-3 text-[12px] text-ink/60">
            Bottles in each hour of the day, noon to noon, over full days · today and the day
            logging began are left out
          </p>
          <PerBaby babies={babies}>
            {(b) => {
              const h = hours.get(b.id);
              if (!h?.days)
                return (
                  <p className={quiet}>
                    {limited
                      ? "No full day of bottles in the last two days."
                      : "No full day of bottles yet. Today and the day logging began are left out."}
                  </p>
                );
              return (
                <>
                  <Suspense fallback={<div className="h-[206px]" aria-hidden="true" />}>
                    <HourBars hours={h} max={max} unit={unit} />
                  </Suspense>
                  <HourTable hours={h} unit={unit} />
                </>
              );
            }}
          </PerBaby>
        </>
      )}
    </Section>
  );
}
